"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Button, ButtonLink } from "@/components/ui/Button";

export type H5PaymentResult = {
  authorizationId: string;
  attemptId: string;
  amountMinor: number;
  state: "reserved" | "creating" | "create_unknown" | "paying" | "paid" | "closed";
  paymentUrl: string | null;
  manualReview?: boolean;
  refundState?: string;
};
export function isH5PaymentResult(value: unknown): value is H5PaymentResult {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.authorizationId !== "string" || !/^[A-Za-z0-9_.:-]{3,256}$/u.test(v.authorizationId)
    || typeof v.attemptId !== "string" || !/^[a-f0-9]{32}$/u.test(v.attemptId)
    || !Number.isSafeInteger(v.amountMinor) || Number(v.amountMinor) <= 0
    || !["reserved", "creating", "create_unknown", "paying", "paid", "closed"].includes(String(v.state))) return false;
  if (v.paymentUrl === null) return true;
  if (typeof v.paymentUrl !== "string") return false;
  try {
    const u = new URL(v.paymentUrl);
    return u.origin === "https://wx.tenpay.com" && u.pathname === "/cgi-bin/mmpayweb-bin/checkmweb" && !u.username && !u.password && !u.hash;
  } catch { return false; }
}
export function rememberH5Payment(userId: number, value: H5PaymentResult) {
  try {
    // Persist only an order locator. Never cache the short-lived payment URL or payment success.
    sessionStorage.setItem(`mira.h5-checkout:${userId}`, JSON.stringify({ authorizationId: value.authorizationId, attemptId: value.attemptId }));
  } catch { /* The authoritative order remains available in the account even without browser storage. */ }
}

const subscribeToBrowser = () => () => undefined;
const isWechatBrowser = () => /MicroMessenger/iu.test(navigator.userAgent);
const serverWechatBrowser = () => false;

export function H5PaymentPanel({ initial, userId }: { initial?: H5PaymentResult; userId: number }) {
  const [payment, setPayment] = useState<H5PaymentResult | null>(initial ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const lastCheck = useRef(0);
  const locator = useRef<{ authorizationId: string; attemptId: string } | null>(initial ?? null);
  const abort = useRef<AbortController | null>(null);
  const insideWechat = useSyncExternalStore(subscribeToBrowser, isWechatBrowser, serverWechatBrowser);

  const refresh = useCallback(async (manual = false) => {
    if (!locator.current || inFlight.current || (!manual && Date.now() - lastCheck.current < 5000)) return;
    inFlight.current = true;
    lastCheck.current = Date.now();
    setBusy(true);
    setError("");
    const controller = new AbortController();
    abort.current = controller;
    try {
      const current = locator.current;
      const response = await fetch(`/api/v1/cloudbase-authorizations/${encodeURIComponent(current.authorizationId)}/h5`, {
        method: "POST", headers: { "Content-Type": "application/json" }, cache: "no-store", signal: controller.signal,
        body: JSON.stringify({ attemptId: current.attemptId }),
      });
      const body: unknown = await response.json();
      if (!response.ok || !isH5PaymentResult(body) || body.authorizationId !== current.authorizationId || body.attemptId !== current.attemptId) throw new Error();
      if (!controller.signal.aborted) setPayment(body);
    } catch {
      if (!controller.signal.aborted) setError("暂时无法确认结果。若已付款请勿再次支付，稍后刷新原订单。");
    } finally {
      inFlight.current = false;
      if (!controller.signal.aborted) setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!locator.current) {
      try {
        const value = JSON.parse(sessionStorage.getItem(`mira.h5-checkout:${userId}`) ?? "null");
        if (value && typeof value.authorizationId === "string" && /^[A-Za-z0-9_.:-]{3,256}$/u.test(value.authorizationId)
          && typeof value.attemptId === "string" && /^[a-f0-9]{32}$/u.test(value.attemptId)) locator.current = value;
      } catch { /* Ignore invalid browser state; it cannot authorize access. */ }
    }
    if (locator.current) void refresh();
    const focus = () => { if (document.visibilityState === "visible") void refresh(); };
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    return () => { window.removeEventListener("focus", focus); document.removeEventListener("visibilitychange", focus); abort.current?.abort(); };
  }, [refresh, userId]);

  if (!payment && !busy && !error) return null;
  const paid = payment?.state === "paid";
  const closed = payment?.state === "closed";
  return <section className="card-surface my-5 rounded-[var(--radius-card)] p-5" aria-label="微信网页付款">
    <h2 className="font-semibold text-[var(--color-paper)]" role="status">{payment?.manualReview ? "付款记录待人工核对" : paid ? "付款已记录" : closed ? "支付单已关闭" : "微信网页付款"}</h2>
    {payment && <p className="mt-2 text-sm">订单金额 ¥{(payment.amountMinor / 100).toFixed(2)}</p>}
    <p className="mt-2 text-sm text-[var(--color-muted)]">{paid ? "已收到付款，请查看订单中的平台审核与授权进度。" : closed ? "请勿再使用此前的付款链接。" : "返回网站后刷新原订单；没有明确结果时，请勿另建订单重复付款。"}</p>
    {insideWechat && !paid && !closed && <p className="mt-2 text-sm">请在手机系统浏览器打开本页付款，或返回小程序查看订单。当前入口不是微信内网页支付。</p>}
    {error && <p role="alert" className="mt-3 text-sm text-[var(--color-pending)]">{error}</p>}
    {payment?.paymentUrl && !paid && !closed && !payment.manualReview && !insideWechat && !error && <ButtonLink href={payment.paymentUrl}>打开微信付款</ButtonLink>}
    <div className="mt-4 flex flex-wrap gap-3">
      <Button type="button" variant="secondary" disabled={busy} onClick={() => void refresh(true)}>我已付款</Button>
      <ButtonLink href="/partner/authorizations" variant="secondary">查看授权订单</ButtonLink>
    </div>
  </section>;
}
